import { capturePath } from "./reportPath";

/**
 * The last failed request, recorded so the Report a problem dialog can carry it (AKL-798).
 *
 * **Why web storage and not the in-memory bus.** The 5xx branch of the response interceptor
 * does `window.location.href = "/unexpectedError"` (`src/api/axios.tsx`) — a full document
 * navigation that tears down the JS heap and every module-scope variable with it. An
 * in-memory bus reaches the toast entry point and nothing else. The interceptor already
 * solves this exact problem once, by writing `unauthorizedMessage` to `localStorage` for
 * `UnauthorizedComponent` to read back and delete ("we cant use context in axios interceptor
 * or an event emitter, so this is best I can think of"). This copies that shape, with
 * `sessionStorage` rather than `localStorage` — the record is about this tab's session and
 * must not outlive it — and a single JSON-encoded record rather than a bare string.
 *
 * Every accessor is wrapped in try/catch: a full, disabled or partitioned storage must never
 * turn a failed request into a second failure.
 */

/** The `sessionStorage` key. Listed in CLAUDE.md's storage-keys table. */
export const LAST_FAILED_REQUEST_KEY = "lastFailedRequest";

/**
 * How long a recorded failure stays attachable.
 *
 * Keep-first has no eraser but a successful send, and `sessionStorage` lives for the life of
 * the tab — so without an age limit a transient 502 from an export poll at 09:05 is still the
 * "last failed request" at 11:30, and the 500 the user is actually reporting is dropped in its
 * favour. Support then receives the wrong failure, with nothing on screen to reveal the
 * substitution.
 *
 * Five minutes: long enough to cover the 5xx hard navigation plus however long someone spends
 * reading the error page before clicking Report this problem, and short enough that a failure
 * from earlier in the session cannot masquerade as the one being reported. Keep-first still
 * protects the real failure *within* the window, which is the case it was added for — a dead
 * API's follow-up `/users/me` failure lands seconds after the one that caused it.
 */
export const FAILED_REQUEST_MAX_AGE_MS = 5 * 60 * 1000;

export type FailedRequestRecord = {
  /** The request path, query string stripped. */
  url: string;
  /** The HTTP method, upper-cased, or "" when the error had no config to read. */
  method: string;
  /** The response status, when there was a response. */
  status?: number;
  /** The axios error code (e.g. `ECONNABORTED`, `ERR_NETWORK`) when there was not. */
  code?: string;
  /** When it was recorded, ISO-8601 UTC. */
  at: string;
};

export type FailedRequestDetails = {
  url: string | undefined;
  method: string | undefined;
  status?: number;
  code?: string;
};

/**
 * Turn raw axios error details into the record shape.
 *
 * Shared by `recordFailedRequest` and by the toast entry point, which attaches **its own**
 * failure rather than reading the stored one. The two must not drift: the toast promises to
 * carry the request its own message is about, and a second shape built by hand at the call
 * site is how that promise quietly stops being true.
 * @param details the method, url and status or error code of the request that failed
 * @returns the record, with the query string stripped from the path
 */
export function buildFailedRequestRecord(
  details: FailedRequestDetails,
): FailedRequestRecord {
  const record: FailedRequestRecord = {
    url: capturePath(details.url),
    method: (details.method ?? "").toUpperCase(),
    at: new Date().toISOString(),
  };
  if (details.status !== undefined) record.status = details.status;
  if (details.code !== undefined) record.code = details.code;
  return record;
}

/**
 * Record a failed request, **keeping the first failure of this document load**.
 *
 * Keep-first, not last-write-wins, and the reason is specific: on `/unexpectedError`,
 * `ProfileProvider` fetches `/users/me` on mount (its guard excludes only `/unauthorized`
 * and review-image routes). When the API is down — the case that produced the 5xx in the
 * first place — that fetch fails too, and a last-write-wins recorder would overwrite the
 * real failure with `GET /users/me`. The background export and migration polls clobber the
 * same slot the same way. Cleared on a successful send, so the next report starts clean.
 *
 * Keep-first applies only **within `FAILED_REQUEST_MAX_AGE_MS`**: an expired record counts as
 * absent, so this failure replaces it. Without that, the first failure of the tab's whole
 * lifetime would own the slot until a report was successfully sent, and every later failure —
 * including the one the user is on the point of reporting — would be dropped.
 * @param details the method, url and status or error code of the request that failed
 */
export function recordFailedRequest(details: FailedRequestDetails): void {
  try {
    // Via readFailedRequest rather than a bare getItem, so an expired record reads as absent
    // (and is cleared) instead of blocking the write.
    if (readFailedRequest() !== null) return;
    sessionStorage.setItem(
      LAST_FAILED_REQUEST_KEY,
      JSON.stringify(buildFailedRequestRecord(details)),
    );
  } catch {
    // Storage full, disabled or unavailable. Losing the prefill is acceptable; throwing from
    // inside the response interceptor is not.
  }
}

/**
 * Read the recorded failure back.
 *
 * Tolerates an absent key, malformed JSON and a partial record — a stale record written by
 * an older build must not crash the dialog.
 *
 * **Expires the record after `FAILED_REQUEST_MAX_AGE_MS`**, returning null and removing the
 * key. A record whose `at` is missing or unparseable is treated as expired too, rather than
 * kept: the point of the window is that nothing of unknown age is ever attached to a report,
 * and a legitimate record always carries a timestamp because `recordFailedRequest` writes
 * one.
 * @returns the record, or null when there is nothing usable or recent enough to read
 */
export function readFailedRequest(): FailedRequestRecord | null {
  try {
    const raw = sessionStorage.getItem(LAST_FAILED_REQUEST_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const candidate = parsed as Partial<FailedRequestRecord>;
    const at = typeof candidate.at === "string" ? candidate.at : "";
    const recordedAt = at ? Date.parse(at) : NaN;
    if (
      Number.isNaN(recordedAt) ||
      Date.now() - recordedAt > FAILED_REQUEST_MAX_AGE_MS
    ) {
      clearFailedRequest();
      return null;
    }
    return {
      url: typeof candidate.url === "string" ? candidate.url : "",
      method: typeof candidate.method === "string" ? candidate.method : "",
      status:
        typeof candidate.status === "number" ? candidate.status : undefined,
      code: typeof candidate.code === "string" ? candidate.code : undefined,
      at,
    };
  } catch {
    return null;
  }
}

/**
 * Remove the recorded failure. Called after a successful send, so a later report does not
 * re-attach a failure the user has already told us about.
 */
export function clearFailedRequest(): void {
  try {
    sessionStorage.removeItem(LAST_FAILED_REQUEST_KEY);
  } catch {
    // See recordFailedRequest.
  }
}
